<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { SbList } from "@unio/saltbox-ui"
import { computed, ref } from "vue"

import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue"
import ListItemRow from "@/components/ListItemRow.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import ResourceShowHeader from "@/components/ResourceShowHeader.vue"
import TagFormSlideover from "@/components/TagFormSlideover.vue"
import { useNavbar } from "@/composables/useNavbar"
import { colorHex } from "@/lib/colors"
import { pluralize } from "@/lib/format"
import { ICON } from "@/lib/icons"
import { tagPath } from "@/routes"
import type { ItemSummary, TagSummary } from "@/types/models"

interface Props {
  tag: TagSummary
  items: ItemSummary[]
  colors: string[]
}

const props = defineProps<Props>()

const { title } = useNavbar({ title: props.tag.name })

const editOpen = ref(false)
const deleteOpen = ref(false)
const deleting = ref(false)

const itemCount = computed(() => pluralize(props.items.length, "item"))

function confirmDelete() {
  deleting.value = true
  router.delete(tagPath({ id: props.tag.id }), {
    onSuccess: () => (deleteOpen.value = false),
    onFinish: () => (deleting.value = false),
  })
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <ResourceShowHeader
      :icon="ICON.tag"
      :icon-style="{ color: colorHex(tag.color_class) }"
      :title="tag.name"
      :subtitle="itemCount"
      @edit="editOpen = true"
      @delete="deleteOpen = true"
    />

    <SbList v-if="items.length" bordered separator>
      <ListItemRow v-for="item in items" :key="item.id" :item="item" variant="compact" />
    </SbList>

    <UEmpty
      v-else
      :icon="ICON.tag"
      title="No items tagged"
      description="Add this tag to items from the item detail page."
    />
  </NarrowPage>

  <TagFormSlideover v-model:open="editOpen" :colors="colors" :tag="tag" />

  <DeleteConfirmModal
    v-model:open="deleteOpen"
    :title="`Delete ${tag.name}?`"
    :loading="deleting"
    @confirm="confirmDelete"
  >
    <p class="text-muted text-sm">
      This will remove the <strong>{{ tag.name }}</strong> tag from all {{ items.length }}
      {{ items.length === 1 ? "item" : "items" }}. This action cannot be undone.
    </p>
  </DeleteConfirmModal>
</template>
